import React, { useState } from "react";
import styled, { keyframes } from "styled-components";
import { colors, tokens } from "../../utils";
import { device } from "../../utils/device";
import BrandWheelLoader from "../BrandWheelLoader";

// Keyframes for fade animation
const fadeAnimation = keyframes`
  0% {
    opacity: 1;
  }
  50% {
    opacity: 0.8;
  }
  100% {
    opacity: 1;
  }
`;

// Styled button component
const StyledButton = styled.button`
  /* DL-004 reach (ISP-4018): this is the app-wide default FILL for every
     normal (non-secondary/outlined/disabled) instance of this shared Button,
     and it always pairs with white label text (see below) - so it is the
     design-system ON-FILL purple tokens.brand.primaryActive #8F38EF
     (white 5.28:1 AA), not the accent colors.PrimaryPurple500 #9B5AF8
     (white 4.03:1, FAILS AA). Root-cause fix: callers that previously
     re-hardcoded colors.PrimaryPurple as an inline style prop still win via
     CSS cascade (inline style beats this class), so nothing else needs to
     change for those; callers relying on this default (e.g. Settings
     "Add Phone Number" / "Change Number" / "Continue" / "Send") are fixed
     for free. */
  background-color: ${tokens.brand.primaryActive};
  color: white;
  outline: none;
  border-radius: 4px;
  border: none;
  min-height: 32px;
  width: 100%;
  /* Without horizontal padding, short labels ("Next" / "Yes" / "Build") in a
     shrink-to-fit flex footer collapse to a ~38px purple chip with clipped or
     invisible text. Callers can still override via the style prop. */
  padding: 0 16px;
  text-align: center;
  font-family: "DM Sans", sans-serif;
  font-weight: 500;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  /* No white-space: nowrap / text-overflow here on purpose (L14 long-string
     overflow spec, buttonLongLabelNoClip.test.jsx) -- a long label wraps
     inside the flex row instead of being clipped by a narrow flex parent
     (e.g. PrimaryWheelPlaceHolder's CTA at 390px, ISP-9999). */
  transition: opacity 0.2s ease;

  /* ISP-9999: 32px min-height falls well under the 44px WCAG-AA touch target
     minimum, measured on the forgot-password submit button at 390px. Bumped
     only at mobile widths (device.sm) to match the app's existing mobile
     touch-target convention (e.g. GuestTranslateButton, SegmentBuilder
     chrome) without resizing every desktop/mouse-driven instance of this
     shared Button. */
  @media ${device.sm} {
    min-height: 44px;
  }

  /* Keyboard-only visible focus ring (WCAG-AA). Mouse focus stays quiet.
     Reads tokens.shadow.focus — the ONE focus-ring source of truth — instead of
     re-hardcoding a palette entry. This used to paint colors.PrimaryPurple300
     (#CBB4FE), which measured 1.83:1 against the white page and 2.89:1 against
     this button's own #8F38EF fill: it cleared NEITHER adjacent boundary. */
  &:focus-visible {
    outline: none;
    box-shadow: ${tokens.shadow.focus};
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
    &.button-clicked {
      animation: none;
    }
  }

  &.secondary {
    background-color: transparent;
    color: #565556;
  }

  &.outlined {
    // PrimaryPurple (#9B5AF8) text on white is only 4.02:1, below the 4.5:1
    // AA floor (axe-core "color-contrast" — measured live on the
    // MobileView/Popup "Have the app? Open here" instance, but this class is
    // shared by every variant="outlined" caller app-wide). PrimaryPurple700
    // (#8126DB, 6.49:1) keeps the same brand hue and passes AA; the border
    // stays PrimaryPurple since non-text UI only needs 3:1.
    color: ${colors.PrimaryPurple700};
    border: 1px solid ${colors.PrimaryPurple};
    background-color: white;
  }

  &.disabled {
    color: #333333;
    background-color: ${colors.lightGrey};
    border: 1px solid ${colors.lightGrey};
  }

  &.button-clicked {
    animation: ${fadeAnimation} 0.5s ease;
  }
`;

const Button = ({
  onClick = () => {},
  isLoading,
  children,
  disabled,
  secondary,
  variant,
  type = "button",
  loaderSize = 20,
  width,
  style,
  ...props
}) => {
  const [isButtonClicked, setIsButtonClicked] = useState(false);

  // On-brand loading indicator (filled Wheel, QA-104) instead of the generic MUI
  // spinner. NEVER spins (brand law) — inline filled Wheel that fills segment
  // by segment; prefers-reduced-motion is honoured by BrandWheelLoader itself.
  // tone tracks the button surface: the FILLED default button (purple #8F38EF
  // fill + white label) needs the dark tone (white ink on the outline path /
  // navy-scrim contrast); the outlined/secondary/disabled variants sit on a
  // light fill and need the light tone. (The old spinner was always white —
  // invisible on the white-background outlined/secondary buttons; that latent
  // bug is what tone= fixes here, same fix as before, new prop name.)
  const isFilledButton =
    !secondary &&
    variant !== "secondary" &&
    variant !== "outlined" &&
    !disabled;

  const handleClick = (e) => {
    if (type !== "submit") {
      e.stopPropagation();
    }
    setIsButtonClicked(true);
    onClick(e);
    setTimeout(() => {
      setIsButtonClicked(false);
    }, 500);
  };

  return (
    <StyledButton
      disabled={disabled || isLoading}
      type={type}
      className={`${secondary ? "secondary" : ""} ${
        isButtonClicked ? "button-clicked" : ""
      } ${variant} ${disabled ? "disabled" : ""} ${
        width ? "w-auto" : "w-full"
      }`}
      onClick={handleClick}
      style={style}
      {...props}
    >
      {isLoading && (
        <BrandWheelLoader
          variant="inline"
          fill="mono"
          size={loaderSize}
          tone={isFilledButton ? "dark" : "light"}
          label="Loading"
          style={{ marginRight: 4 }}
        />
      )}
      {children}
    </StyledButton>
  );
};

export default Button;
